import React, { useState } from 'react';
import PageTemplate from '../components/Common/PageTemplate';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Heart,
  Users,
  Calendar,
  MapPin,
  ExternalLink,
  Award,
  TreePine,
  Eye,
  ChevronRight,
  ChevronLeft,
  X,
  Star,
  Shield,
  Waves,
  Search,
  Globe2,
  Camera,
  Navigation,
  Quote
} from 'lucide-react';

const ekadProject = {
  id: 'ekad',
  heroImage: 'https://fs.hacettepe.edu.tr/fenyeni/ekad/1.jpg',
  trackingLink: 'https://my.wildlifecomputers.com/data/map/?id=66997ef2a1d1bec2fb0b9616',

  gallery: [
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/1.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/2.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/3.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/4.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/5.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/6.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/7.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/8.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/9.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/10.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/11.jpg'
  ],
};

const alanEkolojisiProject = {
  id: 'alan-ekolojisi',
  heroImage: 'https://fs.hacettepe.edu.tr/fenyeni/ekad/6.jpg',
  gallery: [
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/7.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/8.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/ekad/9.jpg',
  ],
};

const betuyabInfo = {
  logo: "https://turkiyeturizmansiklopedisi.com/uploads/img/2023/07/04/c2155598a9ab21d62c072774bfe41082cd340530.jpg"
};

const ParallaxHero = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const { t } = useTranslation();

  return (
    <div className="relative h-[500px] overflow-hidden">
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${ekadProject.heroImage})`, y }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-white text-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
            <Waves className="w-5 h-5 mr-2" />
            <span className="text-sm font-semibold">{t('social_responsibility.ekad.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-tight">
            {t('social_responsibility.ekad.title')}
          </h1>
          <p className="text-lg md:text-xl text-white max-w-3xl mx-auto bg-black/50 p-4 rounded-lg backdrop-blur-sm">
            {t('social_responsibility.ekad.description')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const HacettepeCollaborationSection = () => {
  const { t } = useTranslation();

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('social_responsibility.collaboration.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('social_responsibility.collaboration.lead')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <motion.div
              className="bg-white p-6 rounded-xl shadow-md"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <Globe2 className="w-6 h-6 mr-3 text-blue-500" />
                {t('social_responsibility.collaboration.academicVolunteer')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t('social_responsibility.ekad.collaboration')}
              </p>
            </motion.div>
            <motion.div
              className="bg-white p-6 rounded-xl shadow-md"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <Award className="w-6 h-6 mr-3 text-green-500" />
                {t('social_responsibility.collaboration.fieldAndScience')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t('social_responsibility.ekad.studentImpact')}
              </p>
            </motion.div>
          </div>

          <div>
            <img
              src="https://fs.hacettepe.edu.tr/fenyeni/ekad/10.jpg"
              alt={t('social_responsibility.collaboration.imageAlt')}
              className="rounded-xl shadow-lg w-full h-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ActivitiesSection = () => {
    const { t } = useTranslation();
    const activities = t('social_responsibility.ekad.activities', { returnObjects: true });

    const getIcon = (index) => {
        const icons = [Shield, Star, Eye, Users, Shield, Navigation];
        const Icon = icons[index];
        return <Icon className="w-8 h-8 text-white" />;
    };

    return (
        <div className="py-16">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                      {t('social_responsibility.activities.title')}
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        {t('social_responsibility.activities.subtitle')}
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {activities.map((activity, index) => (
                        <motion.div
                            key={index}
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex items-start space-x-4"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ y: -5 }}
                        >
                            <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-4 rounded-xl">
                                {getIcon(index)}
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">{activity.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{activity.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const GallerySection = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  return (
    <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
            <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                  {t('social_responsibility.gallery.title')}
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    {t('social_responsibility.gallery.subtitle')}
                </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ekadProject.gallery.map((image, index) => (
                    <motion.div
                        key={index}
                        className="overflow-hidden rounded-lg cursor-pointer h-48"
                        onClick={() => { setIsOpen(true); setPhotoIndex(index); }}
                        whileHover={{ scale: 1.05 }}
                    >
                        <img src={image} alt={`Gallery image ${index + 1}`} className="w-full h-full object-cover" />
                    </motion.div>
                ))}
            </div>
        </div>

        {isOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
                <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 text-white z-50"><X size={30} /></button>
                <button onClick={() => setPhotoIndex((photoIndex + ekadProject.gallery.length - 1) % ekadProject.gallery.length)} className="absolute left-4 text-white z-50"><ChevronLeft size={40} /></button>
                <button onClick={() => setPhotoIndex((photoIndex + 1) % ekadProject.gallery.length)} className="absolute right-4 text-white z-50"><ChevronRight size={40} /></button>

                <div className="relative">
                    <img src={ekadProject.gallery[photoIndex]} alt="" className="max-w-screen-lg max-h-screen-xl"/>
                </div>
            </div>
        )}
    </div>
  );
};

const ScientificWorkSection = () => {
  const { t } = useTranslation();

  return (
    <div className="py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('social_responsibility.science.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('social_responsibility.science.lead')}
          </p>
        </div>
        <div className="bg-white p-8 rounded-2xl shadow-lg grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-gray-800">{t('social_responsibility.science.satelliteTitle')}</h3>
            <p className="text-gray-700 leading-relaxed">{t('social_responsibility.ekad.scientificWork')}</p>
            <motion.a
              href={ekadProject.trackingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-teal-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 font-semibold shadow-lg"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Navigation className="w-5 h-5 mr-2" />
              <span>{t('social_responsibility.science.viewMap')}</span>
              <ExternalLink className="w-4 h-4 ml-2" />
            </motion.a>
          </div>
          <div>
            <img src="https://fs.hacettepe.edu.tr/fenyeni/ekad/11.jpg" alt={t('social_responsibility.science.imageAlt')} className="rounded-lg shadow-md w-full h-auto" />
          </div>
        </div>
      </div>
    </div>
  )
}

const BetuyabSection = () => {
  const { t } = useTranslation();

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('social_responsibility.partners.title')}
          </h2>
        </div>
        <div className="max-w-4xl mx-auto bg-white p-8 rounded-2xl shadow-lg flex flex-col md:flex-row items-center gap-8">
          <div className="md:w-1/4 flex justify-center">
            <img src={betuyabInfo.logo} alt="BETUYAB Logo" className="h-24"/>
          </div>
          <div className="md:w-3/4">
            <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('social_responsibility.partners.betuyab.name')}</h3>
            <p className="text-gray-700 leading-relaxed">{t('social_responsibility.partners.betuyab.description')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CallToActionSection = () => {
  const { t } = useTranslation();

  return (
    <div className="py-16 bg-gradient-to-r from-blue-600 to-teal-600">
      <div className="container mx-auto px-4 text-center text-white">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('social_responsibility.cta.title')}</h2>
        <p className="text-lg max-w-2xl mx-auto mb-8">
          {t('social_responsibility.cta.subtitle')}
        </p>
        <div className="flex justify-center space-x-4">
          <motion.a
            href="https://ekad.org.tr/projeler/gonullu-programi/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-blue-600 font-bold py-3 px-8 rounded-full shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('social_responsibility.cta.becomeVolunteer')}
          </motion.a>
          <motion.a
            href="https://ekad.org.tr/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/20 text-white font-bold py-3 px-8 rounded-full"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('social_responsibility.cta.learnMore')}
          </motion.a>
        </div>
      </div>
    </div>
  )
}

const AlanEkolojisiSection = () => {
  const { t } = useTranslation();
  const features = t('social_responsibility.fieldEcology.features', { returnObjects: true });

  const icons = [Search, TreePine, Globe2, Users];

  return (
    <div className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('social_responsibility.fieldEcology.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('social_responsibility.fieldEcology.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
          <div>
            <img
              src={alanEkolojisiProject.heroImage}
              alt="Alan Ekolojisi Eğitimi"
              className="rounded-xl shadow-lg w-full h-auto"
            />
          </div>
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800">{t('social_responsibility.fieldEcology.purposeTitle')}</h3>
            <p className="text-gray-700 leading-relaxed">
              {t('social_responsibility.fieldEcology.longDescription')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300"
                whileHover={{ y: -5 }}
              >
                <div className="inline-block bg-gradient-to-br from-green-500 to-emerald-500 p-4 rounded-full mb-4">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SosyalSorumlulukProjeleri = () => {
  const { t } = useTranslation();

  return (
    <PageTemplate
      title={t('social_responsibility.pageTitle')}
      subtitle={t('social_responsibility.pageSubtitle')}
    >
      <div className="bg-white">
        <ParallaxHero />
        <HacettepeCollaborationSection />
        <AlanEkolojisiSection />
        <div className="py-12">
            <div className="container mx-auto px-4">
                <div className="border-t-2 border-gray-200"></div>
            </div>
        </div>
        <ActivitiesSection />
        <GallerySection />
        <ScientificWorkSection />
        <BetuyabSection />
        <CallToActionSection />
      </div>
    </PageTemplate>
  );
};

export default SosyalSorumlulukProjeleri;
